(() => {
  const config = {
    desktopBreakpoint: 990,
    pinStartProgress: 0.12,
    approachDistance: 220,
    transitionEndViewportRatio: 0.18,
    fixedScale: 1.025,
    magnetDistanceRatio: 0.04,
    positionLerp: 0.12,
    scaleLerp: 0.1,
    handoffProgress: 0.995,
    inertiaEnabled: true,
    inertiaMax: 5,
    inertiaVelocityFactor: 0.2,
    inertiaFollowFactor: 0.18,
    inertiaDecayFactor: 0.16,
    inertiaFadeStart: 0.75,
    inertiaFadeEnd: 0.95
  };

  const selectors = {
    hero: '.ft-hero',
    heroProduct: '.ft-hero__product',
    missionVision: '.ft-purpose',
    showcase: '.ft-product-showcase',
    showcaseProduct: '.ft-product-showcase__product-static'
  };

  const lifecycleEvent = 'fortune-tail:scroll-narrative:destroy';
  document.dispatchEvent(new CustomEvent(lifecycleEvent));

  let controller = null;
  let rebuildFrame = null;
  let rebuildGeneration = 0;

  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));
  const mix = (start, end, progress) => start + (end - start) * progress;
  const smoothstep = (progress) => progress * progress * (3 - 2 * progress);

  const waitForImage = (image) => {
    if (image.complete && image.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  };

  const createController = async () => {
    const hero = document.querySelector(selectors.hero);
    const heroProduct = hero?.querySelector(selectors.heroProduct);
    const missionVision = document.querySelector(selectors.missionVision);
    const showcase = document.querySelector(selectors.showcase);
    const showcaseProduct = showcase?.querySelector(selectors.showcaseProduct);
    const desktopQuery = window.matchMedia(`(min-width: ${config.desktopBreakpoint}px) and (hover: hover) and (pointer: fine)`);
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!hero || !heroProduct || !missionVision || !showcase || !showcaseProduct || !desktopQuery.matches || reducedMotionQuery.matches) return null;

    await waitForImage(heroProduct);
    if (!document.documentElement.contains(hero) || !heroProduct.getAttribute('src')) return null;

    const floating = document.createElement('div');
    const floatingInertia = document.createElement('div');
    const floatingInner = document.createElement('div');
    const floatingImage = document.createElement('img');
    floating.className = 'ft-scroll-product';
    floating.setAttribute('aria-hidden', 'true');
    floatingInertia.className = 'ft-scroll-product__inertia';
    floatingInner.className = 'ft-scroll-product__float';
    floatingImage.className = 'ft-scroll-product__image';
    floatingImage.alt = '';
    floatingImage.src = heroProduct.currentSrc || heroProduct.src;
    floatingInner.appendChild(floatingImage);
    floatingInertia.appendChild(floatingInner);
    floating.appendChild(floatingInertia);
    document.body.appendChild(floating);
    await waitForImage(floatingImage);

    showcase.classList.add('is-scroll-narrative-enabled');

    let destroyed = false;
    let updateFrame = null;
    let measureFrame = null;
    let resizeObserver = null;
    let measurements = null;
    let hasCompletedHandoff = false;
    const state = {
      initialized: false,
      x: 0,
      y: 0,
      scale: 1,
      previousScrollY: window.scrollY,
      inertiaCurrent: 0,
      inertiaTarget: 0
    };

    const readDocumentRect = (element) => {
      const rect = element.getBoundingClientRect();
      return {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      };
    };

    const measure = () => {
      measureFrame = null;
      if (destroyed) return;

      const heroRect = readDocumentRect(hero);
      const sourceRect = readDocumentRect(heroProduct);
      const showcaseRect = readDocumentRect(showcase);
      const destinationRect = readDocumentRect(showcaseProduct);
      if (!sourceRect.width || !sourceRect.height || !destinationRect.width || !destinationRect.height) return;

      const pinStart = heroRect.top + heroRect.height * config.pinStartProgress;
      const pinLeft = sourceRect.left - window.scrollX;
      const pinTop = sourceRect.top - pinStart;
      const transitionStart = showcaseRect.top - window.innerHeight + config.approachDistance;
      const transitionEnd = showcaseRect.top - window.innerHeight * config.transitionEndViewportRatio;

      measurements = {
        heroHeight: heroRect.height,
        sourceWidth: sourceRect.width,
        sourceHeight: sourceRect.height,
        sourceTop: sourceRect.top,
        sourceLeft: sourceRect.left,
        pinStart,
        pinLeft,
        pinTop,
        transitionStart,
        transitionEnd: Math.max(transitionStart + 1, transitionEnd),
        destination: destinationRect
      };

      floating.style.setProperty('--ft-scroll-product-width', `${sourceRect.width}px`);
      floating.style.setProperty('--ft-scroll-product-height', `${sourceRect.height}px`);
      scheduleUpdate(true);
    };

    const scheduleMeasure = () => {
      if (destroyed || measureFrame !== null) return;
      measureFrame = window.requestAnimationFrame(measure);
    };

    const calculateTarget = () => {
      const scrollY = window.scrollY;
      const beforePin = scrollY < measurements.pinStart;
      const transitionProgress = clamp(
        (scrollY - measurements.transitionStart) /
        (measurements.transitionEnd - measurements.transitionStart)
      );
      const easedTransition = smoothstep(transitionProgress);
      const magnetProgress = clamp(
        (scrollY - measurements.pinStart) /
        (measurements.heroHeight * config.magnetDistanceRatio)
      );
      const stableScale = mix(1, config.fixedScale, smoothstep(magnetProgress));
      const stableX = measurements.pinLeft - measurements.sourceWidth * (stableScale - 1) / 2;
      const stableY = measurements.pinTop - measurements.sourceHeight * (stableScale - 1) / 2;
      const destinationX = measurements.destination.left - window.scrollX;
      const destinationY = measurements.destination.top - scrollY;
      const destinationScale = measurements.destination.width / measurements.sourceWidth;
      const settlingProgress = clamp((transitionProgress - 0.84) / 0.16);
      const settlingOffset = Math.sin(settlingProgress * Math.PI) * 9;

      if (beforePin) {
        return {
          active: false,
          pinned: false,
          handoff: false,
          transitionProgress,
          inertiaActive: false,
          x: measurements.sourceLeft - window.scrollX,
          y: measurements.sourceTop - scrollY,
          scale: 1,
          shadowOpacity: 0.2,
          shadowBlur: 26,
          shadowScale: 1.08,
          shadowY: 10
        };
      }

      const landingWeight = Math.sin(settlingProgress * Math.PI);

      return {
        active: transitionProgress < config.handoffProgress,
        pinned: transitionProgress === 0,
        handoff: transitionProgress >= config.handoffProgress,
        transitionProgress,
        inertiaActive: transitionProgress < config.handoffProgress,
        x: mix(stableX, destinationX, easedTransition),
        y: mix(stableY, destinationY, easedTransition) + settlingOffset,
        scale: mix(stableScale, destinationScale, easedTransition),
        shadowOpacity: mix(0.2, 0.24, easedTransition) + landingWeight * 0.019,
        shadowBlur: mix(26, 20, easedTransition) - landingWeight * 2,
        shadowScale: mix(1.08, 0.99, easedTransition),
        shadowY: mix(10, 5, easedTransition) - landingWeight * 2
      };
    };

    const render = (immediate = false) => {
      updateFrame = null;
      if (destroyed || !measurements) return;

      const target = calculateTarget();
      if (!state.initialized || immediate) {
        state.x = target.x;
        state.y = target.y;
        state.scale = target.scale;
        state.initialized = true;
      } else {
        state.x += (target.x - state.x) * config.positionLerp;
        state.y += (target.y - state.y) * config.positionLerp;
        state.scale += (target.scale - state.scale) * config.scaleLerp;
      }

      floating.style.transform = `translate3d(${state.x.toFixed(3)}px, ${state.y.toFixed(3)}px, 0) scale(${state.scale.toFixed(5)})`;

      if (!target.inertiaActive || !config.inertiaEnabled) state.inertiaTarget = 0;
      state.inertiaCurrent += (state.inertiaTarget - state.inertiaCurrent) * config.inertiaFollowFactor;
      state.inertiaTarget += (0 - state.inertiaTarget) * config.inertiaDecayFactor;

      const inertiaFadeProgress = clamp(
        (target.transitionProgress - config.inertiaFadeStart) /
        (config.inertiaFadeEnd - config.inertiaFadeStart)
      );
      const inertiaStrength = target.inertiaActive ? 1 - smoothstep(inertiaFadeProgress) : 0;
      const finalInertia = state.inertiaCurrent * inertiaStrength;
      const breatheStrength = 1 - clamp(Math.abs(finalInertia) / config.inertiaMax) * 0.6;
      floating.style.setProperty('--ft-scroll-inertia-y', `${finalInertia.toFixed(3)}px`);
      floating.style.setProperty('--ft-scroll-breathe-y', `${(-2 * breatheStrength).toFixed(3)}px`);
      floating.style.setProperty('--ft-scroll-shadow-opacity', target.shadowOpacity.toFixed(3));
      floating.style.setProperty('--ft-scroll-shadow-blur', `${target.shadowBlur.toFixed(2)}px`);
      floating.style.setProperty('--ft-scroll-shadow-scale', target.shadowScale.toFixed(4));
      floating.style.setProperty('--ft-scroll-shadow-y', `${target.shadowY.toFixed(2)}px`);
      floating.classList.toggle('is-active', target.active || target.handoff);
      floating.classList.toggle('is-pinned', target.pinned && target.active);
      floating.classList.toggle('is-handoff', target.handoff);
      showcase.classList.toggle('is-scroll-product-revealed', target.handoff);

      if (target.handoff && !hasCompletedHandoff) {
        hasCompletedHandoff = true;
        showcase.classList.add('is-scroll-handoff-complete');
      }

      const unsettled =
        Math.abs(target.x - state.x) > 0.08 ||
        Math.abs(target.y - state.y) > 0.08 ||
        Math.abs(target.scale - state.scale) > 0.0001 ||
        Math.abs(state.inertiaCurrent) > 0.02 ||
        Math.abs(state.inertiaTarget) > 0.02;
      if (unsettled) updateFrame = window.requestAnimationFrame(() => render(false));
    };

    const scheduleUpdate = (immediate = false) => {
      if (destroyed) return;
      if (immediate) {
        if (updateFrame !== null) window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(() => render(true));
      } else if (updateFrame === null) {
        updateFrame = window.requestAnimationFrame(() => render(false));
      }
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollVelocity = currentScrollY - state.previousScrollY;
      state.previousScrollY = currentScrollY;

      if (config.inertiaEnabled && measurements && currentScrollY >= measurements.pinStart) {
        state.inertiaTarget = clamp(
          -scrollVelocity * config.inertiaVelocityFactor,
          -config.inertiaMax,
          config.inertiaMax
        );
      }

      scheduleUpdate(false);
    };
    const handleResize = () => scheduleMeasure();
    const handleMediaChange = () => rebuild();
    const handleTargetImageLoad = () => scheduleMeasure();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    desktopQuery.addEventListener('change', handleMediaChange);
    reducedMotionQuery.addEventListener('change', handleMediaChange);
    showcaseProduct.addEventListener('load', handleTargetImageLoad);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      [hero, missionVision, showcase, heroProduct, showcaseProduct].forEach((element) => resizeObserver.observe(element));
    }

    if (document.fonts?.ready) document.fonts.ready.then(scheduleMeasure);
    measure();

    return {
      destroy() {
        destroyed = true;
        if (updateFrame !== null) window.cancelAnimationFrame(updateFrame);
        if (measureFrame !== null) window.cancelAnimationFrame(measureFrame);
        resizeObserver?.disconnect();
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
        desktopQuery.removeEventListener('change', handleMediaChange);
        reducedMotionQuery.removeEventListener('change', handleMediaChange);
        showcaseProduct.removeEventListener('load', handleTargetImageLoad);
        showcase.classList.remove('is-scroll-narrative-enabled', 'is-scroll-product-revealed', 'is-scroll-handoff-complete');
        floating.remove();
      }
    };
  };

  const rebuild = () => {
    const generation = ++rebuildGeneration;
    controller?.destroy();
    controller = null;
    createController().then((nextController) => {
      if (generation === rebuildGeneration) {
        controller = nextController;
      } else {
        nextController?.destroy();
      }
    });
  };

  const scheduleRebuild = () => {
    if (rebuildFrame !== null) window.cancelAnimationFrame(rebuildFrame);
    rebuildFrame = window.requestAnimationFrame(() => {
      rebuildFrame = null;
      rebuild();
    });
  };

  const destroyAll = () => {
    rebuildGeneration += 1;
    if (rebuildFrame !== null) window.cancelAnimationFrame(rebuildFrame);
    controller?.destroy();
    controller = null;
    document.removeEventListener('shopify:section:load', scheduleRebuild);
    document.removeEventListener('shopify:section:unload', scheduleRebuild);
    document.removeEventListener('shopify:section:reorder', scheduleRebuild);
    document.removeEventListener(lifecycleEvent, destroyAll);
  };

  document.addEventListener('shopify:section:load', scheduleRebuild);
  document.addEventListener('shopify:section:unload', scheduleRebuild);
  document.addEventListener('shopify:section:reorder', scheduleRebuild);
  document.addEventListener(lifecycleEvent, destroyAll, { once: true });
  rebuild();
})();
